import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressCurrencyConverterApiV1CurrencyConverterUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/currency-converter',
        destPath: '',
      },
    ],
    'cronulla-jobs._base_domain_': [
      {
        srcPath: '/currency-converter-bo',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    'currency-converter.wixapps.net': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    'editor._base_domain_': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/currency_converter',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/currency-converter',
        destPath: '',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_currencies';

/** Returns an array of currencies. The array lists all currencies for which Wix supports conversion and their symbols. */
export function listCurrencies(payload: object): RequestOptionsFactory<any> {
  function __listCurrencies({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecommerce.currency_converter.v1.currency_rate',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.currency.converter.api.v1.CurrencyConverter.ListCurrencies',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCurrencyConverterApiV1CurrencyConverterUrl({
        protoPath: '/v1/currencies',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __listCurrencies;
}

/**
 * Returns an array of amounts converted from the original (`from`) currency to the target (`to`) currency and the timestamp for the conversion rate used.
 *
 *
 * Use the `convertCurrency()` function to convert an array of one or more amounts between two currencies. The `convertCurrency()` function returns an array of converted amounts and the timestamp for the conversion rate used.
 *
 * > **Note**:  The currency codes used must exist in the array of supported currencies returned by the [`listCurrencies()`](#listcurrencies) function.
 *
 */
export function convertCurrency(payload: object): RequestOptionsFactory<any> {
  function __convertCurrency({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecommerce.currency_converter.v1.currency_rate',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.currency.converter.api.v1.CurrencyConverter.ConvertCurrency',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCurrencyConverterApiV1CurrencyConverterUrl({
        protoPath: '/v1/currencies/amounts/{from}/convert/{to}',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'rateTimestamp' }],
          },
        ]),
    };

    return metadata;
  }

  return __convertCurrency;
}

/** Returns the conversion rate between 2 currencies. */
export function conversionRate(payload: object): RequestOptionsFactory<any> {
  function __conversionRate({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecommerce.currency_converter.v1.currency_rate',
      method: 'GET' as any,
      methodFqn:
        'com.wixpress.currency.converter.api.v1.CurrencyConverter.ConversionRate',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressCurrencyConverterApiV1CurrencyConverterUrl({
        protoPath: '/v1/currencies/rate/{from}/convert/{to}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [{ path: 'rateTimestamp' }],
          },
        ]),
    };

    return metadata;
  }

  return __conversionRate;
}
